// modules/admin/OrderStatusUpdateModal.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Card,
  CardContent,
  Divider,
  Stack,
  IconButton,
  Chip,
  Paper,
  Avatar,
  AvatarGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Close as CloseIcon,
  LocalShipping as ShippingIcon,
  Inventory as InventoryIcon,
  CheckCircle as ReceivedIcon,
  HourglassEmpty as InTransitIcon,
  Cancel as CancelledIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
  Event as EventIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Photo as PhotoIcon,
  Visibility as ViewIcon,
  AddPhotoAlternate as AddPhotoIcon,
} from '@mui/icons-material';
import { useUpdate, useNotify, useRefresh, useGetOne } from 'react-admin';

const OrderStatusUpdateModal = ({
  open,
  onClose,
  selectedOrder,
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [updating, setUpdating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [viewImageDialog, setViewImageDialog] = useState({
    open: false,
    image: null,
  });
  const fileInputRef = useRef(null);

  // Use react-admin's useUpdate hook
  const [update, { isLoading: updateLoading }] = useUpdate();

  // Fetch order details using react-admin's hook
  const { data: orderDetails, isLoading: loading, error: fetchError, refetch } = useGetOne(
    'orders',
    { id: selectedOrder?.id },
    {
      enabled: open && !!selectedOrder,
    }
  );

  // Set initial status when orderDetails is loaded
  useEffect(() => {
    if (orderDetails?.inKindStatus) {
      setStatus(orderDetails.inKindStatus);
    }
  }, [orderDetails]);

  // Get existing images for current status
  const getExistingImagesForStatus = (statusValue) => {
    if (!orderDetails || !statusValue) return [];
    
    const imageFields = {
      'Dispatched': 'dispatchedImages',
      'InTransit': 'inTransitImages',
      'Delivered': 'deliveredImages',
      'Received': 'receivedImages',
      'Cancelled': [] // No images for cancelled status
    };

    const field = imageFields[statusValue];
    return field && orderDetails[field] ? orderDetails[field] : [];
  };

  // Status options
  const statusOptions = [
    { value: 'Dispatched', label: 'Dispatched', color: 'info', icon: <InventoryIcon /> },
    { value: 'InTransit', label: 'In Transit', color: 'warning', icon: <ShippingIcon /> },
    { value: 'Delivered', label: 'Delivered', color: 'success', icon: <ShippingIcon /> },
    { value: 'Received', label: 'Received', color: 'success', icon: <ReceivedIcon /> },
    { value: 'Cancelled', label: 'Cancelled', color: 'error', icon: <CancelledIcon /> },
  ];

  // Handle file selection
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    // Check total images limit (5 max per status)
    const existingImages = getExistingImagesForStatus(status);
    const totalImages = existingImages.length + selectedImages.length + files.length;
    if (totalImages > 5) {
      notify('Maximum 5 images allowed for this status', { type: 'warning' });
      return;
    }

    // Validate file types and sizes
    const validFiles = files.filter(file => {
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      const maxSize = 10 * 1024 * 1024; // 10MB
      
      if (!validTypes.includes(file.type)) {
        notify(`Invalid file type: ${file.name}. Only images are allowed.`, { type: 'warning' });
        return false;
      }
      
      if (file.size > maxSize) {
        notify(`File too large: ${file.name}. Maximum size is 10MB.`, { type: 'warning' });
        return false;
      }
      
      return true;
    });

    // Create previews for valid files
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setSelectedImages(prev => [...prev, file]);
        setImagePreviews(prev => [...prev, { file, preview: e.target.result }]);
      };
      reader.readAsDataURL(file);
    });

    // Reset file input
    event.target.value = '';
  };

  // Remove selected image
  const removeImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  // Upload images to admin endpoint
  const uploadImages = async () => {
    if (selectedImages.length === 0) return { success: true };

    setUploading(true);
    try {
      const formData = new FormData();
      selectedImages.forEach((file) => {
        formData.append('images', file);
      });

      const apiUrl = process.env.REACT_APP_URL || 'http://localhost:3060';
      const category = status.toLowerCase();
      const token = JSON.parse(localStorage.getItem('token'));

      const response = await fetch(`${apiUrl}/admin/files/upload-images/order/${selectedOrder.id}?category=${category}`, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to upload images');
      }

      const result = await response.json();
      notify('Images uploaded successfully!', { type: 'success' });
      return result;
    } catch (error) {
      notify(error.message || 'Failed to upload images', { type: 'error' });
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!status) {
      notify('Please select a status', { type: 'warning' });
      return;
    }

    try {
      setUpdating(true);
      setError('');
      setSuccess(false);

      // Upload images first if any
      if (selectedImages.length > 0) {
        await uploadImages();
        // Refresh order details to get updated images
        await refetch();
      }

      // Prepare payload for status update
      const payload = {
        inKindStatus: status,
        notes: notes || undefined,
        updatedAt: new Date().toISOString(),
      };

      // Add specific notes based on status if needed
      if (status === 'Dispatched' && notes) {
        payload.dispatchNotes = notes;
      } else if (status === 'Delivered' && notes) {
        payload.deliveryNotes = notes;
      } else if (status === 'Received' && notes) {
        payload.receiptNotes = notes;
      }

      console.log('Updating order with payload:', payload);

      // Use react-admin's update with proper error handling
      await update(
        'orders',
        {
          id: selectedOrder.id,
          data: payload,
          previousData: orderDetails,
        },
        {
          onSuccess: () => {
            setSuccess(true);
            notify('Order status updated successfully!', { type: 'success' });
            refresh(); // Refresh the data
            
            // Clear images and close after success
            setTimeout(() => {
              setSelectedImages([]);
              setImagePreviews([]);
              onClose(true);
            }, 2000);
          },
          onError: (error) => {
            console.error('Update error details:', error);
            setError(error.message || 'Failed to update order status');
            notify(error.message || 'Error updating order status', { type: 'error' });
          },
        }
      );

    } catch (error) {
      console.error('Unexpected error:', error);
      setError('An unexpected error occurred: ' + error.message);
      notify('An unexpected error occurred', { type: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  const getStatusColor = (statusValue) => {
    const statusMap = {
      Dispatched: 'info',
      InTransit: 'warning',
      Delivered: 'success',
      Received: 'success',
      Cancelled: 'error',
    };
    return statusMap[statusValue] || 'default';
  };

  const getOrderType = () => {
    return orderDetails?.negotiation?.type === "Crop" ? "Crop" : 
           orderDetails?.negotiation?.type === "Service" ? "Service" : 
           "In-Kind Contribution";
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getCurrentStatusInfo = () => {
    const currentStatus = statusOptions.find(opt => opt.value === (orderDetails?.inKindStatus || ''));
    if (!currentStatus) return null;
    
    return (
      <Chip
        icon={currentStatus.icon}
        label={currentStatus.label}
        color={getStatusColor(currentStatus.value)}
        variant="outlined"
        sx={{ fontWeight: 'bold' }}
      />
    );
  };

  // Render existing images for the selected status
  const renderExistingImages = () => {
    const existingImages = getExistingImagesForStatus(status);
    if (!existingImages || existingImages.length === 0) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom color="text.secondary">
          Existing Images for {status}:
        </Typography>
        <AvatarGroup max={4} sx={{ justifyContent: 'flex-start' }}>
          {existingImages.map((image, index) => (
            <Avatar
              key={index}
              src={image.startsWith('http') ? image : `${process.env.REACT_APP_API_URL}/${image}`}
              sx={{ 
                width: 56, 
                height: 56,
                cursor: 'pointer',
                '&:hover': { opacity: 0.8 }
              }}
              onClick={() => setViewImageDialog({
                open: true,
                image: image.startsWith('http') ? image : `${process.env.REACT_APP_API_URL}/${image}`
              })}
            >
              <PhotoIcon />
            </Avatar>
          ))}
        </AvatarGroup>
      </Box>
    );
  };

  // Render selected images preview
  const renderSelectedImages = () => {
    if (imagePreviews.length === 0) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom color="text.secondary">
          New Images to Upload ({imagePreviews.length}/5):
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {imagePreviews.map((item, index) => (
            <Box
              key={index}
              sx={{
                position: 'relative',
                width: 80,
                height: 80,
                borderRadius: 1,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <img
                src={item.preview}
                alt={`Preview ${index + 1}`}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
              <IconButton
                size="small"
                onClick={() => removeImage(index)}
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.7)',
                  },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Modal
        open={open}
        onClose={() => !updating && onClose(false)}
        aria-labelledby="order-status-modal"
        maxWidth="md"
        fullWidth
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: 700,
            maxHeight: '90vh',
            overflow: 'auto',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 0,
          }}
        >
          {/* Header */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderBottom: '1px solid',
              borderColor: 'divider',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              position: 'relative',
            }}
          >
            <IconButton
              onClick={() => !updating && onClose(false)}
              sx={{
                position: 'absolute',
                right: 16,
                top: 16,
                color: 'white',
              }}
              disabled={updating}
            >
              <CloseIcon />
            </IconButton>

            <Stack direction="row" spacing={2} alignItems="center">
              <InventoryIcon sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  Update Order Status
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Update the delivery status of this {getOrderType()}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {/* Content */}
          <Box sx={{ p: 3 }}>
            {loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading order details...</Typography>
              </Box>
            ) : fetchError ? (
              <Alert severity="error" sx={{ my: 2 }}>
                Failed to load order details: {fetchError.message}
              </Alert>
            ) : (
              <Grid container spacing={3}>
                {/* Order Details */}
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6" fontWeight="bold">
                          Order #{orderDetails?.id || selectedOrder?.id}
                        </Typography>
                        {getCurrentStatusInfo()}
                      </Stack>
                      
                      <Divider sx={{ my: 2 }} />
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <PersonIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              <strong>Buyer:</strong> {orderDetails?.buyer?.name || selectedOrder?.buyer?.name || 'N/A'}
                            </Typography>
                          </Stack>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <PersonIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              <strong>Seller:</strong> {orderDetails?.negotiation?.seller?.name || selectedOrder?.negotiation?.seller?.name || 'N/A'}
                            </Typography>
                          </Stack>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <CategoryIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              <strong>Type:</strong> {getOrderType()}
                            </Typography>
                          </Stack>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2">
                            <strong>Quantity:</strong> {orderDetails?.quantity || selectedOrder?.quantity || '1'}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2">
                            <strong>Value:</strong> {orderDetails?.price > 0 ? `${orderDetails?.price} SDG` : 'Free'}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <EventIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              <strong>Created:</strong> {formatDate(orderDetails?.createdAt || selectedOrder?.createdAt)}
                            </Typography>
                          </Stack>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Success Message */}
                {success && (
                  <Grid item xs={12}>
                    <Alert severity="success" icon={<ReceivedIcon />}>
                      <Typography variant="body1" fontWeight="bold">
                        Status updated successfully!
                      </Typography>
                      <Typography variant="body2">
                        The order status has been updated to <strong>{status}</strong>.
                        {selectedImages.length > 0 && ' Images have been uploaded.'}
                      </Typography>
                    </Alert>
                  </Grid>
                )}

                {/* Error Message */}
                {error && (
                  <Grid item xs={12}>
                    <Alert severity="error">
                      <Typography variant="body1" fontWeight="bold">
                        Update Failed
                      </Typography>
                      <Typography variant="body2">{error}</Typography>
                    </Alert>
                  </Grid>
                )}

                {/* Status Selection */}
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel id="status-label">Select New Status</InputLabel>
                    <Select
                      labelId="status-label"
                      value={status}
                      label="Select New Status"
                      onChange={(e) => {
                        setStatus(e.target.value);
                        // Clear selected images when status changes
                        if (selectedImages.length > 0) {
                          setSelectedImages([]);
                          setImagePreviews([]);
                        }
                      }}
                      disabled={updating || success}
                    >
                      <MenuItem value="">
                        <em>Select a status...</em>
                      </MenuItem>
                      {statusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            {option.icon}
                            <Typography>{option.label}</Typography>
                          </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Status Description */}
                {status && status !== 'Cancelled' && (
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                          <strong>Image Upload</strong> (Optional)
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          Upload images related to this status update. Maximum 5 images allowed.
                          Supported formats: JPG, PNG, GIF, WebP. Max size: 10MB per image.
                        </Typography>
                        
                        <Box sx={{ mt: 2 }}>
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileSelect}
                            accept="image/*"
                            multiple
                            style={{ display: 'none' }}
                            disabled={updating || uploading || success}
                          />
                          <Button
                            variant="outlined"
                            startIcon={<UploadIcon />}
                            onClick={() => fileInputRef.current?.click()}
                            disabled={updating || uploading || success}
                            sx={{ mb: 2 }}
                          >
                            Select Images
                          </Button>
                          
                          {renderSelectedImages()}
                          {renderExistingImages()}
                          
                          {uploading && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                              <CircularProgress size={20} />
                              <Typography variant="body2" color="text.secondary">
                                Uploading images...
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Notes */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Status Notes (Optional)"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add any notes about this status update..."
                    disabled={updating || success}
                  />
                </Grid>

                {/* Information Alert */}
                <Grid item xs={12}>
                  <Alert severity="info" icon={<InfoIcon />}>
                    <Typography variant="body2">
                      When you update the order status:
                      <ul style={{ margin: '8px 0 0 16px', padding: 0 }}>
                        <li>The order tracking will be updated immediately</li>
                        <li>If you upload images, they will be saved for this specific status</li>
                        <li>The buyer and seller will be notified of the status change</li>
                      </ul>
                    </Typography>
                  </Alert>
                </Grid>
              </Grid>
            )}
          </Box>

          {/* Footer Actions */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
            }}
          >
            <Button
              onClick={() => {
                setSelectedImages([]);
                setImagePreviews([]);
                onClose(false);
              }}
              disabled={updating}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!status || updating || uploading || success}
              variant="contained"
              color="primary"
              startIcon={
                uploading || updating ? (
                  <CircularProgress size={20} color="inherit" />
                ) : success ? (
                  <CheckCircleIcon />
                ) : (
                  <InventoryIcon />
                )
              }
            >
              {uploading ? 'Uploading...' : 
               updating ? 'Updating...' : 
               success ? 'Updated!' : 
               'Update Status'}
            </Button>
          </Paper>
        </Box>
      </Modal>

      {/* Image View Dialog */}
      <Dialog
        open={viewImageDialog.open}
        onClose={() => setViewImageDialog({ open: false, image: null })}
        maxWidth="lg"
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <PhotoIcon />
            <Typography>View Image</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {viewImageDialog.image && (
            <img
              src={viewImageDialog.image}
              alt="Full size"
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewImageDialog({ open: false, image: null })}>
            Close
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => {
              if (viewImageDialog.image) {
                window.open(viewImageDialog.image, '_blank');
              }
            }}
          >
            Open in New Tab
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OrderStatusUpdateModal;